import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, InlineNotification, Modal, NumberInput, Toggle } from '@carbon/react';
import { Link } from 'react-router';
import { api, ApiError } from '../api/client';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../components/states';
import { When } from '../components/format';
import { CommandStatus, HE_A_ERRORS } from '../components/labels';
import type { Command } from './Commands';

// he A config `rut_rui_ro` (backend/lib/rut_rui_ro.js): withdraw rules, holds after security changes,
// player OTP through the linked Telegram bot, risk score per reason and thresholds
type Doc = {
  _ghi_chu?: string;
  tenPhaiTrung: boolean; chiTkDaNapHoacXacMinh: boolean; usdtChoPhep: boolean; lanDauXacNhan: boolean;
  giuGio: { matKhau: number; matKhauRut: number; nganHang: number; telegram: number };
  otp: { rut: boolean; chuyen: boolean; nganHang: boolean; matKhau: boolean; thietBiMoi: boolean; batBuocLienKet: boolean };
  diem: Record<'lanDau' | 'tkMoi' | 'tkChuaNap' | 'tenLech' | 'doiGanDay' | 'thietBiMoi' | 'ipMoi' | 'rutSauNap' | 'soTienLon' | 'usdt', number>;
  nguong: { xacNhan: number; gioTkMoi: number; ngayDoiGanDay: number; phutSauNap: number; soTienLon: number; gioThietBiMoi: number };
};
type Data = {
  noiDung: Doc; otp: { botBat: boolean; bot: string };
  meta: { version: number; source: string; at: string | null }; pending: { id: string; status: string }[];
};

const RULES: { key: 'tenPhaiTrung' | 'chiTkDaNapHoacXacMinh' | 'usdtChoPhep' | 'lanDauXacNhan'; label: string; help: string }[] = [
  { key: 'tenPhaiTrung', label: 'Tên chủ tài khoản phải trùng tên đăng ký', help: 'Tên đăng ký là tên chủ tài khoản ngân hàng liên kết đầu tiên. Liên kết hay rút về tài khoản tên khác bị từ chối.' },
  { key: 'chiTkDaNapHoacXacMinh', label: 'Chỉ rút về tài khoản CSKH đã xác minh', help: 'Sao kê không cho biết tài khoản gửi, nên hệ thống không tự biết người chơi nạp từ đâu. CSKH đối chiếu sao kê (người chơi đã nạp từ tài khoản đó) rồi bấm Xác minh ở trang người chơi; chưa xác minh thì người chơi được báo liên hệ CSKH.' },
  { key: 'usdtChoPhep', label: 'Cho rút USDT', help: 'Rút USDT luôn cộng điểm rủi ro USDT (ví không kiểm được tên chủ).' },
  { key: 'lanDauXacNhan', label: 'Lần rút đầu luôn duyệt tay có xác nhận', help: 'Phải đánh dấu đã kiểm tra trước khi giữ lệnh rút đầu tiên của người chơi.' },
];
const HOLDS: { key: keyof Doc['giuGio']; label: string }[] = [
  { key: 'matKhau', label: 'Sau khi đổi mật khẩu đăng nhập' },
  { key: 'matKhauRut', label: 'Sau khi đổi mật khẩu rút' },
  { key: 'nganHang', label: 'Sau khi đổi / gỡ tài khoản ngân hàng' },
  { key: 'telegram', label: 'Sau khi liên kết Telegram' },
];
const OTPS: { key: keyof Doc['otp']; label: string; help: string }[] = [
  { key: 'rut', label: 'Bắt mã OTP Telegram khi rút tiền', help: 'Bật: mỗi lệnh rút cần mã gửi qua bot Telegram. Tắt: người chơi rút không cần mã, mọi luật rút khác vẫn kiểm như cũ.' },
  { key: 'chuyen', label: 'Bắt mã OTP Telegram khi chuyển tiền cho đại lý', help: 'Công tắc riêng, không đổi theo công tắc rút tiền.' },
  { key: 'nganHang', label: 'Liên kết tài khoản ngân hàng mới', help: '' },
  { key: 'matKhau', label: 'Đổi mật khẩu đăng nhập / mật khẩu rút', help: '' },
  { key: 'thietBiMoi', label: 'Đăng nhập từ thiết bị mới', help: 'Thiết bị đã đăng nhập thành công trước đây không cần mã.' },
  { key: 'batBuocLienKet', label: 'Bắt buộc liên kết Telegram', help: 'Bật: người chưa liên kết Telegram không rút, không đổi ngân hàng / mật khẩu được. Tắt: người chưa liên kết bỏ qua mã.' },
];
const POINTS: { key: keyof Doc['diem']; label: string }[] = [
  { key: 'lanDau', label: 'Lần rút đầu tiên' },
  { key: 'tkMoi', label: 'Tài khoản game mới tạo' },
  { key: 'tkChuaNap', label: 'Rút về tài khoản chưa khai nạp từ đó' },
  { key: 'tenLech', label: 'Tên chủ tài khoản khác tên đăng ký' },
  { key: 'doiGanDay', label: 'Đổi mật khẩu / ngân hàng / Telegram gần đây' },
  { key: 'thietBiMoi', label: 'Thiết bị mới' },
  { key: 'ipMoi', label: 'IP chưa từng đăng nhập' },
  { key: 'rutSauNap', label: 'Rút ngay sau khi nạp' },
  { key: 'soTienLon', label: 'Số tiền lớn' },
  { key: 'usdt', label: 'Rút USDT' },
];
const LIMITS: { key: keyof Doc['nguong']; label: string; max: number; step?: number }[] = [
  { key: 'xacNhan', label: 'Từ số điểm này người duyệt phải xác nhận', max: 100000 },
  { key: 'gioTkMoi', label: 'Tài khoản game mới: dưới bao nhiêu giờ tuổi', max: 8760 },
  { key: 'ngayDoiGanDay', label: 'Đổi bảo mật gần đây: trong bao nhiêu ngày', max: 365 },
  { key: 'phutSauNap', label: 'Rút ngay sau nạp: trong bao nhiêu phút', max: 43200 },
  { key: 'soTienLon', label: 'Số tiền lớn: từ bao nhiêu', max: 1e13, step: 100000 },
  { key: 'gioThietBiMoi', label: 'Thiết bị mới: lần đầu thấy trong bao nhiêu giờ', max: 8760 },
];

const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';
const clamp = (v: unknown, max: number) => Math.min(max, Math.max(0, Math.floor(Number(v) || 0)));

export function WithdrawRiskPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['withdraw-risk'], queryFn: () => api.get<Data>('/api/admin/wallet/risk') });
  const [draft, setDraft] = useState<{ doc: Doc; version: number } | null>(null);
  const [open, setOpen] = useState(false);
  const [notice, setNotice] = useState<{ kind: 'success' | 'warning'; title: string; subtitle: string } | null>(null);
  const live = draft?.doc ?? q.data?.noiDung;
  const set = (p: Partial<Doc>) => live && setDraft({ doc: { ...live, ...p }, version: draft?.version ?? q.data?.meta.version ?? 0 });
  const reload = () => {
    setDraft(null); setOpen(false); qc.invalidateQueries({ queryKey: ['withdraw-risk'] });
    setNotice({ kind: 'warning', title: 'Cấu hình vừa được người khác lưu', subtitle: 'Thay đổi của bạn chưa được lưu. Trang đã tải lại bản mới nhất, hãy sửa lại.' });
  };
  const save = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/wallet/risk', { noiDung: live, expect: draft?.version ?? q.data?.meta.version ?? 0 }),
    onSuccess: (r) => {
      const c = r.command;
      if (c.status === 'FAILED' && c.lastError === 'PHIEN_BAN_CU') return reload();
      setDraft(null); setOpen(false); qc.invalidateQueries({ queryKey: ['withdraw-risk'] });
      setNotice(c.status === 'SUCCEEDED'
        ? { kind: 'success', title: 'Hệ A đã nhận cấu hình mới', subtitle: 'Áp dụng ngay cho lệnh rút, liên kết ngân hàng và đăng nhập tiếp theo.' }
        : { kind: 'warning', title: 'Lệnh chưa xong', subtitle: (c.lastError && HE_A_ERRORS[c.lastError]) || c.lastError || c.status });
    },
    onError: (e) => { if (isStale(e)) reload(); },
  });
  const otp = q.data?.otp;

  return (
    <div className="page">
      <PageHeader title="Rủi ro rút tiền"
        lead="Luật rút, khóa rút sau khi đổi bảo mật, mã OTP qua bot Telegram và điểm rủi ro của lệnh rút. Hệ A kiểm mọi luật lúc người chơi gửi lệnh và lại lúc duyệt; trang này chỉ đổi con số. Số lệnh và số tiền rút mỗi ngày nằm ở trang Tài khoản nhận và hạn mức." />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error || !live ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : (
        <>
          <p className="muted">Phiên bản {q.data?.meta.version} ({q.data?.meta.source === 'cms' ? 'CMS' : 'mặc định'}){q.data?.meta.at ? <> · <When v={q.data.meta.at} /></> : null}</p>
          {notice ? <InlineNotification kind={notice.kind} lowContrast title={notice.title} subtitle={notice.subtitle} onClose={() => setNotice(null)} /> : null}
          {q.data?.pending.length ? <p>Có lệnh đang chờ: {q.data.pending.map((p) => <CommandStatus key={p.id} s={p.status} />)}</p> : null}
          <section className="section">
            <h2>Luật rút</h2>
            <div className="stack">
              {RULES.map((r) => (
                <div key={r.key}>
                  <Toggle id={'rule-' + r.key} labelText={r.label} labelA="Tắt" labelB="Bật" toggled={live[r.key]} onToggle={(v) => set({ [r.key]: v })} />
                  <p className="muted">{r.help}</p>
                </div>
              ))}
            </div>
          </section>
          <section className="section">
            <h2>Khóa rút sau khi đổi bảo mật (giờ)</h2>
            <p className="muted">0 = không khóa. Người chơi thấy giờ được rút lại.</p>
            <div className="stack">
              {HOLDS.map((h) => (
                <NumberInput key={h.key} id={'hold-' + h.key} label={h.label} min={0} max={720} step={1} value={live.giuGio[h.key]}
                  onChange={(_e, { value }) => set({ giuGio: { ...live.giuGio, [h.key]: clamp(value, 720) } })} />
              ))}
            </div>
          </section>
          <section className="section">
            <h2>Mã OTP qua Telegram</h2>
            {otp && !otp.botBat
              ? <InlineNotification kind="warning" lowContrast hideCloseButton title="OTP đang tắt"
                  subtitle="Chưa cấu hình bot Telegram (token và tên bot), nên hệ thống không gửi mã OTP nào. Các công tắc dưới đây có hiệu lực khi bot chạy." />
              : otp ? <p className="muted">Bot đang chạy: @{otp.bot}. Người chơi liên kết Telegram ở hồ sơ, tab Bảo mật.</p> : null}
            <div className="stack">
              {OTPS.map((o) => (
                <div key={o.key}>
                  <Toggle id={'otp-' + o.key} labelText={o.label} labelA="Tắt" labelB="Bật" toggled={live.otp[o.key]} onToggle={(v) => set({ otp: { ...live.otp, [o.key]: v } })} />
                  {o.help ? <p className="muted">{o.help}</p> : null}
                </div>
              ))}
            </div>
          </section>
          <section className="section">
            <h2>Điểm rủi ro theo lý do</h2>
            <p className="muted">Mỗi lệnh rút cộng điểm theo từng lý do khớp; hàng duyệt hiện tổng điểm và lý do.</p>
            <div className="check-grid">
              {POINTS.map((p) => (
                <NumberInput key={p.key} id={'pt-' + p.key} label={p.label} min={0} max={1000} step={5} value={live.diem[p.key]}
                  onChange={(_e, { value }) => set({ diem: { ...live.diem, [p.key]: clamp(value, 1000) } })} />
              ))}
            </div>
          </section>
          <section className="section">
            <h2>Ngưỡng</h2>
            <div className="stack">
              {LIMITS.map((l) => (
                <NumberInput key={l.key} id={'lim-' + l.key} label={l.label} min={0} max={l.max} step={l.step ?? 1} value={live.nguong[l.key]}
                  onChange={(_e, { value }) => set({ nguong: { ...live.nguong, [l.key]: clamp(value, l.max) } })} />
              ))}
            </div>
          </section>
          <p className="muted">Chặn tài khoản ngân hàng, Telegram, thiết bị hoặc IP: <Link to="/risk/blocklist">Danh sách chặn</Link>.</p>
          <div className="row section">
            <Button disabled={!draft} onClick={() => setOpen(true)}>Lưu</Button>
            {draft ? <Button kind="ghost" onClick={() => setDraft(null)}>Bỏ thay đổi</Button> : null}
          </div>
          <Modal open={open} modalHeading="Lưu cấu hình rủi ro rút?" primaryButtonText="Lưu" secondaryButtonText="Hủy"
            primaryButtonDisabled={save.isPending} onRequestClose={() => setOpen(false)} onRequestSubmit={() => save.mutate()}>
            <div className="stack">
              <p>Áp dụng ngay cho lệnh rút, liên kết ngân hàng, đổi mật khẩu và đăng nhập tiếp theo. Lệnh rút đang chờ được chấm lại lúc duyệt.</p>
              <p>Xác nhận từ {live.nguong.xacNhan} điểm · lần rút đầu {live.lanDauXacNhan ? 'luôn xác nhận' : 'theo điểm'} · khóa sau đổi mật khẩu {live.giuGio.matKhau} giờ</p>
              <InlineError error={save.error} />
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
